import { ApiFailure, api, tokens } from './api';
import { getPosition, onAppResume } from './native';

/**
 * 司机位置上报 —— 管理端地图上「司机在哪」的数据来源。
 *
 * ★ **只在手上有单时采**(已接单 / 已取货 / 配送中),空闲、下线一个点都不采。
 *   该不该采由服务端说了算(GET /driver/tracking),这里每分钟问一次;
 *   服务端收点时还会再按同一条规则筛一遍。
 *
 * ★ 采集:每 30 秒取一次位置,先存在本机;攒够 10 个点或满 60 秒批量传一次。
 *   断网时点留在本机,联网后补传(最多留 500 个,再多丢最旧的 —— 不能让手机存储无限涨)。
 *
 * ★ 只在 App 开着时采:锁屏或切到别的 App,系统会暂停网页代码,
 *   采集也就停了。后台持续定位要原生插件(另一个版本的事)。
 */

const SAMPLE_MS = 30_000;
const DECIDE_MS = 60_000;
const FLUSH_COUNT = 10;
const FLUSH_MS = 60_000;
const MAX_QUEUE = 500;
const QUEUE_KEY = 'delivery.driver.track-queue';

interface Point {
  lat: number;
  lng: number;
  accuracyM?: number;
  speedMps?: number;
  heading?: number;
  recordedAt: string;
}

function readQueue(): Point[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as Point[];
  } catch {
    return [];
  }
}
function writeQueue(q: Point[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-MAX_QUEUE)));
  } catch {
    /* 存储满了 / 隐私模式 —— 丢几个点不该影响司机干活 */
  }
}

let track = false;
let started = false;
let flushing: Promise<void> | null = null;
let firstQueuedAt = 0;

/** 把攒着的点传上去。同一时刻只传一批 */
export function flushTrack(): Promise<void> {
  flushing ??= (async () => {
    const q = readQueue();
    if (q.length === 0 || !tokens.get()) return;
    const batch = q.slice(0, 200); // 接口一次最多收 200 个
    try {
      await api('/driver/locations', { method: 'POST', body: { points: batch } });
      // 传成功(包括被服务端按规则丢掉的)就从本机删掉 —— 只删这一批:
      // 传的过程中可能又采了新点。按时间删而不是「删前 N 个」:
      // 队列满了会挤掉最旧的,前 N 个就不一定是刚传的那些了
      const lastSent = batch[batch.length - 1]!.recordedAt;
      writeQueue(readQueue().filter((p) => p.recordedAt > lastSent));
      firstQueuedAt = readQueue().length > 0 ? Date.now() : 0;
    } catch (e) {
      /*
       * ★ 服务端明确说「这批不对」(4xx 校验失败)就扔掉这一批,不能留着重传。
       *   留着的话它永远排在最前面、永远被拒 —— 后面所有的点都跟着堵死,
       *   本机队列只涨不消。(iOS 的速度 -1 就这样堵过一次。)
       *   网络不通、登录过期(401)、被限流(429)才是「下次再试」。
       */
      if (e instanceof ApiFailure && e.status >= 400 && e.status < 500 && e.status !== 401 && e.status !== 429) {
        const lastSent = batch[batch.length - 1]!.recordedAt;
        writeQueue(readQueue().filter((p) => p.recordedAt > lastSent));
        console.warn('[tracking] 服务端拒收了一批位置,已丢弃', e.code);
      }
    }
  })().finally(() => {
    flushing = null;
  });
  return flushing;
}

async function sample(): Promise<void> {
  if (!track) return;
  const p = await getPosition();
  if (!p || !track) return;
  const q = readQueue();
  q.push({
    lat: p.lat,
    lng: p.lng,
    ...(p.accuracyM != null ? { accuracyM: p.accuracyM } : {}),
    ...(p.speedMps != null ? { speedMps: p.speedMps } : {}),
    ...(p.heading != null ? { heading: p.heading } : {}),
    recordedAt: new Date().toISOString(),
  });
  writeQueue(q);
  if (!firstQueuedAt) firstQueuedAt = Date.now();
  // 留几秒余量:第一个点通常比定时器早一点点采(刚接单就采),
  // 卡死 60 秒的话第 60 秒那次差几毫秒不够,第一批要拖到 90 秒才传
  if (q.length >= FLUSH_COUNT || Date.now() - firstQueuedAt >= FLUSH_MS - 5000) void flushTrack();
}

/** 问服务端现在该不该采。变成「不采」的那一刻,把手上的点一次传完 */
export async function refreshTracking(): Promise<void> {
  if (!tokens.get()) {
    track = false;
    return;
  }
  try {
    const r = await api<{ track: boolean }>('/driver/tracking');
    const was = track;
    track = r.track;
    if (track && !was) void sample(); // 刚接了单:别等 30 秒,马上采第一个点
    if (!track && was) void flushTrack(); // 刚送完最后一单 / 下线:尾巴传上去
  } catch {
    /* 问不到就维持原状 —— 断网时继续采,联网后补传 */
  }
}

/**
 * 启动采集循环(App 挂载后调一次)。
 * 真正采不采由 refreshTracking 决定;循环本身很轻,空闲时每分钟一个小请求。
 */
export function startTracking(): void {
  if (started) return;
  started = true;
  void refreshTracking();
  void flushTrack(); // 上次没传完的
  setInterval(() => void sample(), SAMPLE_MS);
  setInterval(() => void refreshTracking(), DECIDE_MS);
  void onAppResume(() => {
    void refreshTracking();
    void flushTrack();
  });
}

/**
 * 下线 / 退出登录前调:先停、再把攒着的点传完。
 * ★ 必须在发「下线」请求**之前**传:下线之后服务端一个点都不收了。
 */
export async function stopTrackingAndFlush(): Promise<void> {
  track = false;
  await flushTrack();
}

/** 退出登录:本机攒的点属于上一个人,不能带给下一个登录的人 */
export function clearTrack(): void {
  track = false;
  writeQueue([]);
  firstQueuedAt = 0;
}
